import { useContext, useMemo, useState } from "react";

import { useDebounce } from "@/hooks/lib-ui";
import { TransactionDetailModal } from "./TransactionDetailModal";
import { GetPaymentHistoryInput, OrdersFilters, PaymentHistory } from "@/types/payment-history.type";
import { FinanceFilter, FinanceFilterContext } from "@/modules/finance/helper/context/finance-filter.context";
import { DataTable } from "@/components/data-table/UniversalDataTable";
import { usePaymentHistory } from "@/modules/finance/helper/hooks/use-payment-history";
import { usePaymentHistoryColumn } from "@/modules/finance/helper/hooks/use-payment-history-column";
import { useSearchFields } from "@/modules/finance/helper/hooks/use-search-fields";
import { CityContext } from "@/contexts/city-context-provider";
import { SortOrder } from "@/types/gql/graphql";

export const TransactionTable = () => {
  const { setCurrentPage, endDate, startDate, sortOrder, sortField, pageIndex, pageSize, setSortOrder } = useContext(
    FinanceFilterContext,
  ) as FinanceFilter;
  const { currentCity } = useContext(CityContext);

  const [value, setValue] = useState<string>("");
  const searchString = useDebounce(value, 500);

  // Filter states for multiple selection
  const [searchFields, setSearchFields] = useState<Partial<OrdersFilters>>();
  const [selectedPaymentHistory, setSelectedPaymentHistory] = useState<PaymentHistory>();

  const paymentHistoryInput: GetPaymentHistoryInput = useMemo(() => {
    return {
      cityId: currentCity?.id,
      pagination: { pageIndex, pageSize },
      filter: {
        startDate,
        endDate,
        ...searchFields,
      },
      sortOrders: { sortField, sortOrder },
    };
  }, [currentCity?.id, endDate, pageIndex, pageSize, searchFields, sortField, sortOrder, startDate]);

  /**
   * Get the history payment
   * **/
  const {
    paymentHistoryData: { filters: conditions = {}, results: data = [] } = {},
    totalPage = 0,
    loading,
  } = usePaymentHistory(paymentHistoryInput);

  const columnNames = usePaymentHistoryColumn();

  useSearchFields(searchString, conditions, setSearchFields);

  return (
    <div className="w-full rounded-2xl border border-icon p-8 bg-white flex flex-col flex-1 overflow-hidden">
      <DataTable
        data={data}
        isLoading={loading}
        columns={columnNames}
        sortOrders={{ sortOrder, sortField }}
        onSortingChange={(value) =>
          setSortOrder({
            sortField: value?.sortField ?? "start_date",
            sortOrder: value?.sortOrder ?? SortOrder.Desc,
          })
        }
        currentPage={{ pageIndex, pageSize }}
        totalPage={totalPage}
        onChangePage={setCurrentPage}
        placeHolder="Filtrage par plaque d'immatriculation..."
        searchString={value}
        onSearchChange={setValue}
        title="Transactions récentes"
        onRowClick={setSelectedPaymentHistory}
      />
      {selectedPaymentHistory && (
        <TransactionDetailModal
          paymentHistory={selectedPaymentHistory}
          onClose={() => setSelectedPaymentHistory(undefined)}
        />
      )}
    </div>
  );
};
